import { faEyeSlash } from '@fortawesome/pro-light-svg-icons/faEyeSlash';
import { faEye } from '@fortawesome/pro-regular-svg-icons/faEye';
import useLocalStorage from '@propertypal/shared/src/hooks/useLocalStorage';
import { ImportantPlaceResponse } from '@propertypal/shared/src/types/important-places';
import { IsoChrone } from '@propertypal/shared/src/types/map';
import wrapApiPolygon from '@propertypal/shared/src/utils/polygons';
import FontAwesomeIcon from '@propertypal/web-ui/src/icons/FontAwesomeIcon';
import { LeafletMarker } from '@propertypal/web-ui/src/map/LeafletMap';
import { config, animated, useSpring } from '@react-spring/web';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import React, { useMemo, useState } from 'react';
import { MapContainer, ShowHideBtn } from './OpenSearchMap.style';

const LeafletMap = dynamic(() => import('@propertypal/web-ui/src/map/LeafletMap'), { ssr: false });

interface OpenSearchMapProps {
  importantPlaces: ImportantPlaceResponse[];
  polygons?: IsoChrone;
  url: string;
  mob?: boolean;
}

const OpenSearchMap: React.FC<OpenSearchMapProps> = ({ importantPlaces, url, mob, polygons }) => {
  const [isVisible, setIsVisible] = useLocalStorage<boolean | ''>('showSearchArea', true);
  const [renderMap, setRenderMap] = useState<boolean>(!!isVisible);
  const router = useRouter();
  const mapUrl = `/map${url}`;
  const leaftletMarkers: LeafletMarker[] = useMemo(() => {
    return importantPlaces?.map((p) => ({
      lat: p.coordinate.latitude,
      lng: p.coordinate.longitude,
      name: p.name,
      searched: !!polygons?.polygonIds?.some((id) => p.polygonIds.includes(id)),
    }));
  }, [importantPlaces]);

  const mapPolygons = useMemo(() => {
    if (polygons?.coordinates?.length) return wrapApiPolygon(polygons?.coordinates);
    return null;
  }, [polygons?.coordinates]);

  const springProps = useSpring({
    config: { ...config.default, tension: 200 },
    from: { opacity: 0, maxHeight: 0 },
    to: {
      opacity: !mob || isVisible ? 1 : 0,
      maxHeight: !mob || isVisible ? 300 : 0,
    },
    onRest: () => {
      setRenderMap(!!isVisible);
    },
    onStart: () => {
      setRenderMap(true);
    },
  });

  if (!polygons?.coordinates?.length || !mapPolygons) return null;

  return (
    <MapContainer data-testid="open-search-map">
      <animated.div style={springProps}>
        {renderMap && (
          <LeafletMap polygons={mapPolygons} markers={leaftletMarkers} onExpand={() => router.push(mapUrl)} />
        )}
      </animated.div>
      {mob && (
        <ShowHideBtn onClick={() => setIsVisible(!isVisible)} isVisible={!!isVisible} data-testid="show-hide-btn">
          <FontAwesomeIcon key={`${isVisible}`} icon={isVisible ? faEyeSlash : faEye} />
          <span>{isVisible ? 'Hide' : 'Show'} Search Area</span>
        </ShowHideBtn>
      )}
    </MapContainer>
  );
};

export default OpenSearchMap;
